import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import DatePicker from "react-multi-date-picker";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { ACTION_ICONS } from "../../config/siteConfig";
import ContentLoader from "../../components/loader/ContentLoader";
import DetailView from "../../components/location/DetailView";
import { enquiriesService } from "../../services/enquiries.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const STATUS_FILTER_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in-progress", label: "In Process" },
  { value: "done", label: "Done" },
  { value: "closed", label: "Closed" },
];

// statuses an open (new / in-progress) enquiry can be moved to
const STATUS_CHANGE_OPTIONS = [
  { value: "in-progress", label: "In Process" },
  { value: "done", label: "Done" },
  { value: "closed", label: "Closed" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const initialFilters = { search: "", enquire_type: "", status: "", from: "", to: "" };

const statusColors = (status) => {
  switch (status) {
    case "done":
      return { bg: "#d4edda", color: "#155724" };
    case "closed":
      return { bg: "#e9ecef", color: "#495057" };
    case "in-progress":
      return { bg: "#cfe2ff", color: "#084298" };
    default:
      return { bg: "#fff3cd", color: "#856404" };
  }
};

const statusLabel = (v) => STATUS_FILTER_OPTIONS.find((o) => o.value === v)?.label || v || "-";
const formatDateTime = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "-");

const StatusPill = ({ value }) => {
  const c = statusColors(value);
  return (
    <span
      className="d-inline-block"
      style={{ background: c.bg, color: c.color, borderRadius: 8, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}
    >
      {statusLabel(value)}
    </span>
  );
};

const Enquiries = () => {
  const { start, complete } = useLoadingBar({ color: "blue", height: 2 });

  const [loading, setLoading] = useState(false);
  const [enquiryRes, setEnquiryRes] = useState({ pagination: {}, enquiries: [] });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const [statusTarget, setStatusTarget] = useState(null);
  const [statusRemarks, setStatusRemarks] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);

  const [show, setShow] = useState(false);
  const [viewEnquiry, setViewEnquiry] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    loadEnquiries();
  }, [pagingLimit, pageValue, appliedFilters]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadEnquiries = async () => {
    try {
      setLoading(true);
      start();
      const res = await enquiriesService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });
      if (res?.success) {
        setEnquiryRes({
          pagination: res.resData?.pagination || {},
          enquiries: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  const handleViewEnquiry = async (enquiryId) => {
    setShow(true);
    setViewEnquiry(null);
    try {
      setViewLoading(true);
      const res = await enquiriesService.details(enquiryId);
      if (res?.success) {
        setViewEnquiry(res.resData);
      } else {
        toast.error(res?.message);
        setShow(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShow(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleStatusChange = (enquiry, newStatus) => {
    if (!newStatus || newStatus === enquiry?.status) return;
    setStatusTarget({ enquiry, status: newStatus });
    setStatusRemarks("");
  };

  const closeStatusModal = () => {
    if (statusSaving) return;
    setStatusTarget(null);
    setStatusRemarks("");
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    if (!statusRemarks.trim()) {
      toast.error("Remarks are required");
      return;
    }
    const result = await withReactContent(Swal).fire({
      title: "Change status?",
      text: `Are you sure you want to mark the enquiry from ${statusTarget.enquiry?.agency_name} as ${statusLabel(statusTarget.status)}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setStatusSaving(true);
      const res = await enquiriesService.updateStatus(statusTarget.enquiry?.id, {
        status: statusTarget.status,
        remarks: statusRemarks.trim(),
      });
      if (res?.success) {
        toast.success(res.message);
        setStatusTarget(null);
        setStatusRemarks("");
        loadEnquiries();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setStatusSaving(false);
    }
  };

  const handleDeleteEnquiry = async (enquiry) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete enquiry?",
      text: `Are you sure you want to delete the enquiry from ${enquiry?.agency_name}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await enquiriesService.delete(enquiry?.id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadEnquiries();
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApply = (e) => {
    e?.preventDefault();
    if (filters.from && filters.to && filters.from > filters.to) {
      toast.error("From date cannot be after To date");
      return;
    }
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const enquiries = enquiryRes?.enquiries || [];
  const totalRecords = enquiryRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const newCount = enquiries.filter((e) => e?.status === "new").length;
  const doneCount = enquiries.filter((e) => e?.status === "done" || e?.status === "closed").length;
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "Type", key: "enquire_type" },
    { label: "Agency Name", key: "agency_name" },
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Phone", key: "phone" },
    { label: "Website", key: "website" },
    { label: "Active Users", key: "active_users" },
    { label: "Monthly Sales", key: "monthly_sales" },
    { label: "Years In Business", key: "years_in_business" },
    { label: "Business Model", key: "business_model" },
    { label: "Remarks", key: "remarks" },
    { label: "Status", key: "status" },
    { label: "Submitted At", key: "created_at" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const filterInput = {
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    fontSize: 13,
    height: 34,
  };

  const dateFilter = (name, placeholder) => (
    <div className="col-6 col-md-2">
      <DatePicker
        onOpenPickNewDate={false}
        value={filters[name] || null}
        onChange={(d) => setFilters({ ...filters, [name]: d ? d.format("YYYY-MM-DD") : "" })}
        format="YYYY-MM-DD"
        placeholder={placeholder}
        editable={false}
        inputClass="form-control"
        containerStyle={{ width: "100%" }}
        style={{ ...filterInput, width: "100%" }}
      />
    </div>
  );

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>
              Enquiries
            </h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Enquiries</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Enquiry List
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <CSVLink
              data={enquiries}
              headers={csvHeaders}
              filename="enquiries.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{
                background: "#fff",
                border: "1px solid #e8edf5",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                color: "#444",
              }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input
                  type="text"
                  className="form-control"
                  name="search"
                  placeholder="Search agency, name, email, phone"
                  value={filters.search}
                  onChange={handleFilterChange}
                  style={filterInput}
                />
              </div>
              <div className="col-6 col-md-2">
                <input
                  type="text"
                  className="form-control"
                  name="enquire_type"
                  placeholder="Enquiry Type"
                  value={filters.enquire_type}
                  onChange={handleFilterChange}
                  style={filterInput}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={STATUS_FILTER_OPTIONS.find((o) => o.value === filters.status) || null}
                  onChange={(o) => setFilters({ ...filters, status: o ? o.value : "" })}
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              {dateFilter("from", "From date")}
              {dateFilter("to", "To date")}
              <div className="col-12 col-md-auto d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Enquiries
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{newCount}</b> New on this page
          </span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{doneCount}</b> Done/Closed on this page
          </span>
        </div>

        <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", "Agency", "Contact", "Business", "Type", "Submitted", "Status", "Actions"].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                          borderBottom: "2px solid #e8edf5",
                          padding: "10px 12px",
                          whiteSpace: "nowrap",
                          background: "#f8f9fe",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((enquiry, sno_i) => (
                    <tr key={enquiry?.id} style={{ borderBottom: "1px solid #f0f2f5" }}>
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: 36, height: 36, background: "#e8f0fe", flexShrink: 0 }}
                          >
                            <i className="bi bi-chat-left-text text-primary" style={{ fontSize: 14 }}></i>
                          </div>
                          <div>
                            <div className="fw-semibold text-dark" style={{ fontSize: 13 }}>
                              {enquiry?.agency_name}
                            </div>
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              {enquiry?.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 13 }}>
                          <i className="bi bi-telephone-fill me-1 text-primary" style={{ fontSize: 10 }}></i>
                          {enquiry?.phone}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {enquiry?.email}
                        </div>
                      </td>
                      <td>
                        <div className="fw-medium text-capitalize" style={{ fontSize: 13 }}>
                          {enquiry?.business_model || "-"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {enquiry?.years_in_business ? `${enquiry.years_in_business} yrs` : "-"}
                          {enquiry?.active_users ? ` · ${enquiry.active_users} users` : ""}
                        </div>
                      </td>
                      <td>
                        <span className="fw-medium text-capitalize" style={{ fontSize: 13 }}>
                          {enquiry?.enquire_type || "-"}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: 13 }}>{formatDateTime(enquiry?.created_at)}</span>
                      </td>
                      <td>
                        {enquiry?.status === "new" || enquiry?.status === "in-progress" ? (
                          <select
                            className="form-select form-select-sm"
                            value={enquiry.status}
                            onChange={(e) => handleStatusChange(enquiry, e.target.value)}
                            style={{
                              width: "auto",
                              minWidth: 0,
                              height: 28,
                              padding: "0 26px 0 10px",
                              backgroundPosition: "right 8px center",
                              borderRadius: 8,
                              fontSize: 12,
                              fontWeight: 600,
                              backgroundColor: statusColors(enquiry.status).bg,
                              color: statusColors(enquiry.status).color,
                              border: "none",
                            }}
                          >
                            {enquiry.status === "new" && (
                              <option value="new" disabled>
                                New
                              </option>
                            )}
                            {STATUS_CHANGE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <StatusPill value={enquiry?.status} />
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleViewEnquiry(enquiry?.id)}
                            title="View"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: "#e6f7f1",
                              color: "#0f8a5f",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                          </button>
                          {/* <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleDeleteEnquiry(enquiry)}
                            title="Delete"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: "#fce8e6",
                              color: "#d93025",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <i className={`bi ${ACTION_ICONS.delete}`} data-action="delete" style={{ fontSize: 13 }}></i>
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && enquiries.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                        No enquiries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> enquiries
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 100, borderRadius: 8 }}
                value={pagingLimit}
                onChange={handlePagingLimit}
              >
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => setPageValue(1)}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => setPageValue(pageValue - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => setPageValue(n)}>
                      {n}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => setPageValue(pageValue + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => setPageValue(totalPage)}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal centered show={!!statusTarget} onHide={closeStatusModal}>
          <form onSubmit={handleStatusSubmit}>
            <Modal.Header closeButton>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Update Status</div>
                <div style={{ fontSize: 12, color: "#8a94a6" }}>
                  {statusTarget?.enquiry?.agency_name} · {statusLabel(statusTarget?.enquiry?.status)} →{" "}
                  {statusLabel(statusTarget?.status)}
                </div>
              </div>
            </Modal.Header>
            <Modal.Body>
              <div style={{ fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#6c757d", marginBottom: 6 }}>
                Remarks <span className="text-danger">*</span>
              </div>
              <textarea
                className="form-control"
                rows={4}
                autoFocus
                placeholder="Add a note about this status change"
                value={statusRemarks}
                onChange={(e) => setStatusRemarks(e.target.value)}
                style={{ borderRadius: 8, border: "1px solid #dfe3ec", fontSize: 13 }}
              />
            </Modal.Body>
            <Modal.Footer className="justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-sm px-3"
                onClick={closeStatusModal}
                disabled={statusSaving}
                style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-sm px-3 text-white"
                disabled={statusSaving}
                style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}
              >
                {statusSaving ? "Saving..." : "Update Status"}
              </button>
            </Modal.Footer>
          </form>
        </Modal>

        <Modal size="lg" centered show={show} onHide={() => setShow(false)}>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{
              background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)",
              borderBottom: "none",
            }}
          >
            <Modal.Title
              className="d-flex align-items-center gap-2"
              style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
            >
              <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i> Enquiry Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 24, background: "#f8f9fe" }}>
            {viewLoading || !viewEnquiry ? (
              <ContentLoader loaderType="circleSpin" />
            ) : (
              <DetailView
                fields={[
                  { label: "Enquiry Type", value: viewEnquiry.enquire_type },
                  { label: "Agency Name", value: viewEnquiry.agency_name },
                  { label: "Name", value: viewEnquiry.name },
                  { label: "Email", value: viewEnquiry.email },
                  { label: "Phone", value: viewEnquiry.phone },
                  { label: "Website", value: viewEnquiry.website },
                  { label: "Active Users", value: viewEnquiry.active_users },
                  { label: "Monthly Sales", value: viewEnquiry.monthly_sales },
                  { label: "Years In Business", value: viewEnquiry.years_in_business },
                  { label: "Business Model", value: viewEnquiry.business_model },
                  { label: "Remarks", value: viewEnquiry.remarks },
                  { label: "Submitted At", value: formatDateTime(viewEnquiry.created_at) },
                  { label: "Status", value: statusLabel(viewEnquiry.status) },
                ]}
              />
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  );
};

export default Enquiries;
